import styled from 'styled-components';
import {Modal} from '@ui/molecules/modal';
import {Image} from '@ui/atoms/image';
import {illustrationList} from '@ui/assets/illustrations';
import {Button} from '@ui/atoms/button';
import {Body2, Headline3} from '@ui/atoms/typography';
import {down} from 'styled-breakpoints';
import {getColorFromTheme} from '@ui/ui-utils';
import {useEffect, useRef} from 'react';

const StatusFrame = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 24px;

  ${down('sm')} {
    padding: 16px;
  }
`;

const ModalBody = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 850px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(180px, 0.9fr);
  align-items: center;
  gap: clamp(24px, 4vw, 48px);
  padding: clamp(24px, 4vw, 40px);

  ${down('md')} {
    grid-template-columns: minmax(0, 1fr);
  }

  ${down('sm')} {
    padding: 32px 24px;
  }
`;

const Copy = styled.div`
  min-width: 0;
`;

const Description = styled(Body2)`
  color: ${getColorFromTheme('textBody')};
`;

const Illustration = styled.div`
  display: flex;
  justify-content: flex-end;

  img {
    width: min(100%, 350px);
    height: auto;
  }

  ${down('md')} {
    display: none;
  }
`;

const StatusAction = styled(Button)`
  margin-top: 24px;
  border-radius: 8px;
  padding: 8px 16px;

  ${down('sm')} {
    width: 100%;
  }
`;

interface SubscriptionExpiredModalProps {
  open: boolean;
  onUpdateDetails: () => void;
}

interface StatusModalProps {
  open: boolean;
  title: string;
  description: string;
  subDescription?: string;
  buttonText?: string;
  titleId: string;
  onAction?: () => void;
}

const StatusModal = ({
  open,
  title,
  description,
  subDescription,
  buttonText,
  titleId,
  onAction,
}: StatusModalProps) => {
  const actionRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const keepFocusOnAction = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      event.preventDefault();
      (actionRef.current || dialogRef.current)?.focus();
    };

    (actionRef.current || dialogRef.current)?.focus();
    document.addEventListener('keydown', keepFocusOnAction, true);
    return () =>
      document.removeEventListener('keydown', keepFocusOnAction, true);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={() => (actionRef.current || dialogRef.current)?.focus()}
      fullHeight
      zIndex={100}>
      <StatusFrame>
        <ModalBody
          id="modal-card"
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}>
          <Copy>
            <Headline3 id={titleId}>{title}</Headline3>
            <Description className="mt-4">{description}</Description>
            {subDescription && (
              <Description className="mt-8">{subDescription}</Description>
            )}
            {buttonText && onAction && (
              <StatusAction ref={actionRef} onClick={onAction} autoFocus>
                {buttonText}
              </StatusAction>
            )}
          </Copy>
          <Illustration>
            <Image {...illustrationList.painting} alt="" aria-hidden="true" />
          </Illustration>
        </ModalBody>
      </StatusFrame>
    </Modal>
  );
};

export const SubscriptionExpiredModal = ({
  open,
  onUpdateDetails,
}: SubscriptionExpiredModalProps) => {
  return (
    <StatusModal
      open={open}
      title="Subscription ended"
      description="Your subscription has ended, but your workspace and data are safe."
      subDescription="Review billing and choose a plan to restore access to Beam."
      buttonText="Review billing"
      titleId="subscription-ended-title"
      onAction={onUpdateDetails}
    />
  );
};

interface TrialEndedModalProps {
  open: boolean;
  onBuyPlan: () => void;
  workspaceName: string;
}

export const TrialEndedModal = ({
  open,
  onBuyPlan,
  workspaceName,
}: TrialEndedModalProps) => {
  return (
    <StatusModal
      open={open}
      title="Free Trial has ended"
      description={`The free trial for ${workspaceName} has ended, but your workspace and data are safe.`}
      subDescription="Choose a plan to keep using Beam with your team."
      buttonText="Choose a plan"
      titleId="trial-ended-title"
      onAction={onBuyPlan}
    />
  );
};

interface PaymentRequiredModalProps {
  open: boolean;
  canManageBilling: boolean;
  onReviewBilling: () => void;
}

export const PaymentRequiredModal = ({
  open,
  canManageBilling,
  onReviewBilling,
}: PaymentRequiredModalProps) => (
  <StatusModal
    open={open}
    title="Payment required"
    description="This workspace has an outstanding payment. Access is paused until the balance is paid."
    subDescription={
      canManageBilling
        ? 'Review billing to retry the payment or update the payment method.'
        : 'Ask a workspace administrator to resolve the outstanding payment.'
    }
    buttonText={canManageBilling ? 'Review billing' : undefined}
    titleId="payment-required-title"
    onAction={canManageBilling ? onReviewBilling : undefined}
  />
);
